import { useEffect, useCallback, useState, useRef } from "react";
import { X, ExternalLink, MapPin, Clock, Check, Share2, Star, Repeat, Bell, Ticket } from "lucide-react";
import { formatTimeRange } from "@/lib/dateUtils";
import { DanceStartLine } from "./DanceStartLine";
import { formatPriceWithUnit } from "@/lib/price";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { EventStyleBadges } from "./DanceStyleBadge";
import { useFavorites } from "@/hooks/useFavorites";
import { EVENT_TAG_MAP } from "@/lib/constants";
import { ImageLightbox } from "../ui/ImageLightbox";
import { RemindMeModal } from "./RemindMeModal";
import type { Event } from "./EventCard";

interface EventModalProps {
  event: Event | null;
  onClose: () => void;
}

export function EventModal({ event, onClose }: EventModalProps) {
  const [copied, setCopied] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const {
    isEventFavorited,
    toggleEventFavorite,
    isVenueFavorited,
    toggleVenueFavorite,
    canFavorite,
  } = useFavorites();
  const isFavorited = event ? isEventFavorited(event) : false;
  const isVenueFav = event ? isVenueFavorited(event.venueId) : false;

  // Following needs an account. Signed out, the controls become a way in
  // rather than a button that appears to do nothing.
  const goSignIn = () => {
    window.location.href = "/login";
  };

  useEffect(() => {
    if (!event) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    closeButtonRef.current?.focus();
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", handleTab);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleTab);
      document.body.style.overflow = "";
    };
  }, [event, onClose]);

  const shareUrl = event ? `${window.location.origin}/events?event=${event._id}` : window.location.href;

  const handleCopyLink = useCallback(async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [shareUrl]);

  const handleShare = useCallback(async () => {
    if (event && navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: `${event.title} at ${event.venueName}`,
          url: shareUrl,
        });
      } catch {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  }, [event, shareUrl, handleCopyLink]);

  if (!event) return null;

  if (lightboxOpen && event.flyerUrl) {
    return (
      <ImageLightbox
        src={event.flyerUrl}
        alt={event.title}
        onClose={() => setLightboxOpen(false)}
      />
    );
  }

  const priceLabel = formatPriceWithUnit(event.priceMin, event.priceMax, event.priceUnit);

  const formattedDate = new Date(event.date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          className="bg-card rounded-xl border border-border/50 overflow-hidden h-full sm:h-auto max-h-[calc(100vh-2rem)] flex flex-col"
        >
          {/* Header */}
          <div className="relative p-5 pb-4 border-b border-border/50">
            {/* Header buttons */}
            <div className="absolute top-4 right-4 flex items-center gap-1">
              <button
                onClick={() =>
                  canFavorite
                    ? void toggleEventFavorite(event._id, event.title)
                    : goSignIn()
                }
                className={cn(
                  "p-2 rounded-lg transition-colors duration-200",
                  isFavorited
                    ? "text-primary"
                    : "text-muted-foreground hover:text-primary hover:bg-muted/50"
                )}
                aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
              >
                <Star
                  className="w-5 h-5"
                  fill={isFavorited ? "currentColor" : "none"}
                />
              </button>
              <button
                ref={closeButtonRef}
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Dance style badges */}
            <div className="flex flex-wrap gap-1.5 mb-3 pr-20">
              <EventStyleBadges danceStyles={event.danceStyles} mainStyles={event.mainStyles} size="md" />
            </div>

            {/* Title */}
            <h2 id="modal-title" className="font-display text-2xl text-foreground leading-tight mb-2">
              {event.title}
            </h2>

            {/* Tags */}
            {event.tags && event.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {event.tags.map((slug) => {
                  const tag = EVENT_TAG_MAP[slug];
                  if (!tag) return null;
                  return (
                    <span
                      key={slug}
                      className="text-xs font-medium text-muted-foreground bg-secondary px-2.5 py-1 rounded-full border border-border/50 whitespace-nowrap"
                    >
                      {tag.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Date & Time */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <Clock className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">{formattedDate}</p>
                <p className="text-sm text-muted-foreground">
                  {formatTimeRange(event.startTime, event.endTime)}
                </p>
                <DanceStartLine event={event} variant="full" className="mt-0.5" />
              </div>
            </div>

            {/* Admission */}
            {priceLabel && (
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-secondary">
                  <Ticket className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{priceLabel}</p>
                  {event.priceMax !== undefined &&
                    event.priceMin !== undefined &&
                    event.priceMax !== event.priceMin && (
                      <p className="text-sm text-muted-foreground">
                        Price varies — check with the organizer
                      </p>
                    )}
                </div>
              </div>
            )}

            {/* Venue */}
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <MapPin className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-foreground">{event.venueName}</p>
                  {event.venueId && (
                    <button
                      onClick={() =>
                        canFavorite
                          ? void toggleVenueFavorite(event.venueId!, event.venueName)
                          : goSignIn()
                      }
                      className={cn(
                        "p-1 rounded transition-colors duration-200",
                        isVenueFav
                          ? "text-primary"
                          : "text-muted-foreground/40 hover:text-primary"
                      )}
                      aria-label={isVenueFav ? "Remove venue from favorites" : "Add venue to favorites"}
                      title={isVenueFav ? "Remove venue from favorites" : "Favorite this venue"}
                    >
                      <Star
                        className="w-3.5 h-3.5"
                        fill={isVenueFav ? "currentColor" : "none"}
                      />
                    </button>
                  )}
                </div>
                {event.venueAddress && (
                  <p className="text-sm text-muted-foreground">{event.venueAddress}</p>
                )}
              </div>
            </div>

            {/* Recurrence info */}
            {event.isRecurring && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Repeat className="w-4 h-4" />
                <span>Recurring event</span>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className="pt-2">
                <p className="text-foreground/80 text-sm leading-relaxed whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>
            )}

            {/* Flyer */}
            {event.flyerUrl && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  className="w-full rounded-lg overflow-hidden border border-border/50 hover:border-primary/50 transition-colors cursor-zoom-in"
                  aria-label="View full flyer"
                >
                  <img
                    src={event.flyerUrl}
                    alt="Event flyer"
                    className="w-full object-contain max-h-80"
                  />
                </button>
              </div>
            )}

            {/* Source links */}
            {event.sourceLinks && event.sourceLinks.length > 0 && (
              <div className="pt-2 space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">View on:</p>
                {event.sourceLinks.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="p-5 pt-4 bg-card-footer flex flex-col sm:flex-row gap-2">
            {event.eventUrl && (
              <Button asChild className="flex-1">
                <a href={event.eventUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                  View Event Page
                </a>
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setRemindOpen(true)}
              className={cn(event.eventUrl ? "sm:w-auto" : "flex-1")}
            >
              <Bell className="w-4 h-4" />
              Remind me
            </Button>
            <Button
              variant="outline"
              onClick={handleShare}
              className={cn(event.eventUrl ? "sm:w-auto" : "flex-1")}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4" />
                  Share
                </>
              )}
            </Button>
          </div>
        </div>
      {remindOpen && (
        <RemindMeModal event={event} onClose={() => setRemindOpen(false)} />
      )}
      </div>
    </div>
  );
}
